import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, Globe, Eye, EyeOff, Shield } from 'lucide-react';

const AdminAuthPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language, toggleLanguage, localize } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(formData.email, formData.password);
      
      if (user.role !== 'admin') {
        toast.error(localize({ ar: 'هذا الحساب ليس حساب مسؤول', en: 'This is not an admin account' }));
        return;
      }
      
      toast.success(localize({ ar: 'تم تسجيل الدخول بنجاح', en: 'Login successful' }));
      navigate('/admin');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail[language] || detail.ar : detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6 ${language === 'ar' ? 'rtl' : 'ltr'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/login" className="inline-flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-slate-600 to-slate-700 flex items-center justify-center border border-slate-500">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div className="text-left">
              <span className="text-2xl font-bold text-white">
                {localize({ ar: 'دخول المسؤول', en: 'Admin Login' })}
              </span>
              <span className="block text-xs text-slate-400">Learn With Trust</span>
            </div>
          </Link>
        </div>

        <Card className="border-0 shadow-2xl bg-slate-800/50 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-end mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-slate-400 hover:text-white"
              >
                <Globe className="w-4 h-4 mr-1" />
                {language === 'ar' ? 'English' : 'العربية'}
              </Button>
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              {localize({ ar: 'لوحة التحكم', en: 'Control Panel' })}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {localize({ ar: 'دخول مسؤولي المنصة فقط', en: 'Platform administrators only' })}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">
                  {localize({ ar: 'البريد الإلكتروني', en: 'Email' })}
                </Label>
                <div className="relative">
                  <Mail className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500`} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@trustedtechno.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10' : 'pl-10'} h-12 rounded-xl bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-slate-500`}
                    required
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">
                  {localize({ ar: 'كلمة المرور', en: 'Password' })}
                </Label>
                <div className="relative">
                  <Lock className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500`} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12 rounded-xl bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-slate-500`}
                    required
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-500 hover:to-slate-600 rounded-xl font-semibold text-base border border-slate-500"
                disabled={loading}
                data-testid="admin-login-submit"
              >
                {loading 
                  ? (localize({ ar: 'جاري الدخول...', en: 'Logging in...' })) 
                  : (localize({ ar: 'دخول', en: 'Login' }))
                }
              </Button>
            </form>

            <div className="mt-6 p-4 bg-slate-700/30 rounded-xl border border-slate-600">
              <p className="text-sm text-slate-400 text-center">
                🔒 {localize({ ar: 'هذه الصفحة مخصصة لمسؤولي المنصة فقط. الوصول غير المصرح به ممنوع.', en: 'This page is for platform administrators only. Unauthorized access is prohibited.' })
                }
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className={`w-4 h-4 ${language === 'ar' ? '' : 'rotate-180'}`} />
            {localize({ ar: 'العودة لاختيار نوع الحساب', en: 'Back to account selection' })}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminAuthPage;
